import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";

/**
 * Servicio de acceso al backend de Champion's Journey (CHAMP).
 * Todas las llamadas usan doFetchJson de ConnectionManager y devuelven
 * el envelope { STATE, MESSAGE, DATA }.
 */

const APP = "CHAMP";

/** Construye un body application/x-www-form-urlencoded a partir de un objeto. */
function formBody(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) sp.append(k, v);
  });
  return sp.toString();
}

function postForm(method, params) {
  return doFetchJson(APP, method, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: formBody(params),
  });
}

function putForm(method, params) {
  return doFetchJson(APP, method, {
    method: "PUT",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: formBody(params),
  });
}

function get(method) {
  return doFetchJson(APP, method, { method: "GET" });
}

function del(method) {
  return doFetchJson(APP, method, { method: "DELETE" });
}

// ─── Partidas ───────────────────────────────────────────────────────────────

export const getMyGames = () => get("my-games");

export const createGame = ({ nombre, regionInicio, nombrePersonaje }) =>
  postForm("games", { nombre, regionInicio, nombrePersonaje });

export const joinGame = ({ codigo, nombrePersonaje }) =>
  postForm("games/join", { codigo, nombrePersonaje });

export const getGame = (id) => get("games/" + id);
export const getGameByCodigo = (codigo) => get("games/code/" + encodeURIComponent(codigo));

export const deleteGame = (id) => del("games/" + id);

export const leaveGame = (id) =>
  postForm("games/" + id + "/leave", {});

export const updateGameConfig = (id, fields) =>
  putForm("games/" + id + "/config", fields);

export const joinGamePublic = (codigo, nombrePersonaje) =>
  postForm("games/code/" + encodeURIComponent(codigo) + "/join", { nombrePersonaje });

// ─── Personaje ──────────────────────────────────────────────────────────────

export const getCharacter = (id) => get("characters/" + id);

export const updateCharacter = (id, fields) =>
  putForm("characters/" + id, fields);

// ─── Pokémon del personaje ────────────────────────────────────────────────────

export const addPokemon = (charId, { idPokemon, apodo, enEquipo, nivel }) =>
  postForm("characters/" + charId + "/pokemon", { idPokemon, apodo, enEquipo, nivel });

export const updatePokemon = (ppId, fields) =>
  putForm("characters/pokemon/" + ppId, fields);

export const removePokemon = (ppId) => del("characters/pokemon/" + ppId);

export const addEgg = (charId, { idPokemon, turnosHuevo }) =>
  postForm("characters/" + charId + "/egg", { idPokemon, turnosHuevo });

export const updateEggTurnos = (ppId, turnosHuevo) =>
  putForm("characters/pokemon/" + ppId + "/egg-turnos", { turnosHuevo });

/** Eclosiona un huevo convirtiéndolo en el Pokémon indicado (o el pre-asignado). */
export const hatchEgg = (ppId, idPokemon) =>
  postForm("characters/pokemon/" + ppId + "/hatch", idPokemon != null ? { idPokemon } : {});

/** Evoluciona un Pokémon del personaje al Pokémon indicado. */
export const evolveCharacterPokemon = (ppId, idPokemonEvolucion) =>
  postForm("characters/pokemon/" + ppId + "/evolve", { idPokemonEvolucion });

/** Intercambia zona (equipo/caja) y slot entre dos Pokémon del personaje. */
export const swapPokemon = (idPpA, idPpB) =>
  putForm("characters/pokemon/swap", { idPpA, idPpB });

/** Cura todos los Pokémon del personaje (vida a máximo y sin debilitar). */
export const healAllPokemon = (charId) =>
  postForm("characters/" + charId + "/heal-all", {});

/** Reordena el equipo según una lista ordenada de idPp. */
export const reorderTeam = (charId, orderedPpIds) =>
  putForm("characters/" + charId + "/team-order", { order: (orderedPpIds || []).join(",") });

// ─── Mochila / objetos ────────────────────────────────────────────────────────

export const setItem = (charId, { idObjeto, cantidad }) =>
  putForm("characters/" + charId + "/items", { idObjeto, cantidad });

/** Usa un objeto utilizable sobre un Pokémon: aplica el efecto y descuenta 1 unidad. */
export const useItem = (charId, { idObjeto, idPp }) =>
  postForm("characters/" + charId + "/use-item", { idObjeto, idPp });

// ─── Estados de Pokémon ─────────────────────────────────────────────────────────

export const addEstado = (ppId, idEstado) =>
  postForm("characters/pokemon/" + ppId + "/estados", { idEstado });

export const removeEstado = (ppId, idEstado) =>
  del("characters/pokemon/" + ppId + "/estados/" + idEstado);

export const getCatalogEstados = () => get("catalog/estados");

// ─── Medallas ─────────────────────────────────────────────────────────────────

export const setBadge = (charId, badgeId, obtenida) =>
  putForm("characters/" + charId + "/badges/" + badgeId, { obtenida });

// ─── Tarjetas Journey ─────────────────────────────────────────────────────────

export const addCard = (charId, idTarjeta) =>
  postForm("characters/" + charId + "/cards", { idTarjeta });

export const removeCard = (ptId) => del("characters/cards/" + ptId);

// ─── Catálogo ─────────────────────────────────────────────────────────────────

export const getCatalogPokemon = (region) =>
  get("catalog/pokemon" + (region ? "?region=" + encodeURIComponent(region) : ""));

export const getCatalogPokemonDetail = (idPokemon) =>
  get("catalog/pokemon/" + idPokemon);

export const getCatalogItems = () => get("catalog/items");

export const getCatalogBadges = (region) =>
  get("catalog/badges" + (region ? "?region=" + encodeURIComponent(region) : ""));

export const getCatalogCards = () => get("catalog/cards");

export const getCatalogRegiones = () => get("catalog/regiones");

// ─── Administración de catálogo ───────────────────────────────────────────────

export const adminSavePokemon = (idPokemon, fields) => {
  const method = idPokemon > 0 ? "catalog/pokemon/" + idPokemon : "catalog/pokemon";
  const httpMethod = idPokemon > 0 ? "PUT" : "POST";
  return doFetchJson(APP, method, {
    method: httpMethod,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: formBody(idPokemon > 0 ? fields : { idPokemon, ...fields }),
  });
};

export const adminDeletePokemon = (idPokemon) =>
  del("catalog/pokemon/" + idPokemon);

export const adminSaveItem = (idObjeto, fields) => {
  const method = idObjeto > 0 ? "catalog/items/" + idObjeto : "catalog/items";
  const httpMethod = idObjeto > 0 ? "PUT" : "POST";
  return doFetchJson(APP, method, {
    method: httpMethod,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: formBody(fields),
  });
};

export const adminDeleteItem = (idObjeto) =>
  del("catalog/items/" + idObjeto);

export const adminSaveBadge = (idMedalla, fields) => {
  const method = idMedalla > 0 ? "catalog/badges/" + idMedalla : "catalog/badges";
  const httpMethod = idMedalla > 0 ? "PUT" : "POST";
  return doFetchJson(APP, method, {
    method: httpMethod,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: formBody(fields),
  });
};

export const adminDeleteBadge = (idMedalla) =>
  del("catalog/badges/" + idMedalla);

export const adminSaveCard = (idTarjeta, fields) => {
  const method = idTarjeta > 0 ? "catalog/cards/" + idTarjeta : "catalog/cards";
  const httpMethod = idTarjeta > 0 ? "PUT" : "POST";
  return doFetchJson(APP, method, {
    method: httpMethod,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: formBody(fields),
  });
};

export const adminDeleteCard = (idTarjeta) =>
  del("catalog/cards/" + idTarjeta);

export function isOk(res) {
  return res && (res.STATE === "SUCCESS" || res.STATE === "WARNING");
}
